// ResearchSeo.jsx
import React, { useEffect } from "react";

const ensureMetaByName = (name) => {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  return el;
};

const ensureMetaByProperty = (prop) => {
  let el = document.head.querySelector(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    document.head.appendChild(el);
  }
  return el;
};

const ensureJsonLd = (id = "research-jsonld") => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  return el;
};

export default function ResearchSeo({ research, doctor, canonicalUrl }) {
  useEffect(() => {
    if (!research) return;

    const title =
      (research.title ? `${research.title} – ` : "") + "Research • MikDok";
    const description =
      research.description?.slice(0, 160) ||
      "Research paper on MikDok.";
    const url = canonicalUrl || window.location.href;

    // Title
    document.title = title;

    // Basic meta
    ensureMetaByName("description").setAttribute("content", description);

    // Open Graph
    ensureMetaByProperty("og:title").setAttribute("content", title);
    ensureMetaByProperty("og:description").setAttribute("content", description);
    ensureMetaByProperty("og:type").setAttribute("content", "article");
    ensureMetaByProperty("og:url").setAttribute("content", url);

    // Pick a preview image if any image file exists
    const img =
      (Array.isArray(research.files) &&
        research.files.find((f) => /\.(png|jpe?g|webp|gif|bmp|svg)(\?.*)?$/i.test(f))) ||
      null;
    if (img) {
      ensureMetaByProperty("og:image").setAttribute("content", img);
    }

    // Twitter
    ensureMetaByName("twitter:card").setAttribute("content", img ? "summary_large_image" : "summary");
    ensureMetaByName("twitter:title").setAttribute("content", title);
    ensureMetaByName("twitter:description").setAttribute("content", description);
    if (img) ensureMetaByName("twitter:image").setAttribute("content", img);

    // JSON-LD structured data
    const ld = {
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      name: research.title || "Research",
      description,
      url,
      datePublished: research.createdAt
        ? new Date(research.createdAt).toISOString()
        : undefined,
      image: img ? [img] : undefined,
      author: doctor
        ? {
            "@type": "Person",
            name: doctor.name || "Doctor",
            jobTitle: doctor.specialty || undefined,
            image: doctor.photoURL || undefined,
          }
        : undefined,
      interactionStatistic: [
        research.views != null && {
          "@type": "InteractionCounter",
          interactionType: "https://schema.org/LikeAction", // not perfect, but shows popularity
          userInteractionCount: Number(research.views) || 0,
        },
        research.downloads != null && {
          "@type": "InteractionCounter",
          interactionType: "https://schema.org/DownloadAction",
          userInteractionCount: Number(research.downloads) || 0,
        },
      ].filter(Boolean),
    };

    const jsonldEl = ensureJsonLd();
    jsonldEl.text = JSON.stringify(ld, null, 2);
  }, [research, doctor, canonicalUrl]);

  return null;
}
